<!-- The toolbar renders for the manager drill too (isSelf false), which shows this
     same grid for a report. Only the ARRANGE controls below are self-only: the drill
     persists no layout. The period switcher is not an edit control, and gating it on
     isSelf would silently move a manager's view of a report from month to week with
     nothing to change it back. -->
<div class="cfg-toolbar">
  <!-- One period for every widget that can answer in one (#1075). The as-of-today
       widgets (timesheet, heatmap, assignments) are NOT driven by this: they answer
       the same thing whatever period they are sent, so moving the switcher would
       leave them visibly unchanged with nothing saying why. Each states its own
       window in its subtitle instead. -->
  <div class="period-switch" role="group" *ngIf="!editing">
    <button nbButton type="button" shape="semi-round" size="small"
            [status]="selectedPeriod === 'week' ? 'primary' : 'basic'"
            [attr.aria-pressed]="selectedPeriod === 'week'"
            (click)="setPeriod('week')">{{ 'dashboard.unit.week' | translate }}</button>
    <button nbButton type="button" shape="semi-round" size="small"
            [status]="selectedPeriod === 'month' ? 'primary' : 'basic'"
            [attr.aria-pressed]="selectedPeriod === 'month'"
            (click)="setPeriod('month')">{{ 'dashboard.unit.month' | translate }}</button>
  </div>
  @if (isSelf) {
    <span class="cfg-hint" *ngIf="editing">{{ 'dashboard.self.arrange_hint' | translate }}</span>
    <!-- Only when the stored layout actually differs from the current default. Opens the
         default as a preview in Arrange; nothing is written until Save. -->
    <button *ngIf="!editing && defaultDiffers" nbButton type="button" shape="semi-round" size="small" status="basic" (click)="previewDefaultLayout()">{{ 'dashboard.self.layout.use_default' | translate }}</button>
    <button *ngIf="!editing" nbButton type="button" shape="semi-round" size="small" status="primary" (click)="enterArrange()">{{ 'edit' | translate }}</button>
    <button *ngIf="editing && layoutEditable" nbButton type="button" shape="semi-round" size="small" status="basic" (click)="resetLayout()">{{ 'dashboard.self.layout.reset' | translate }}</button>
    <button *ngIf="editing" nbButton type="button" shape="semi-round" size="small" status="basic" (click)="cancelArrange()">{{ 'cancel' | translate }}</button>
    <button *ngIf="editing" nbButton type="button" shape="semi-round" size="small" status="primary" [disabled]="savingLayout || !selfLayoutLoaded" (click)="saveLayout()">{{ 'save' | translate }}</button>
    <!-- Save is disabled until the layout GET settles (selfLayoutLoaded): this makes the
         "click Save during the load window" path unreachable from the UI, so saveLayout's
         !selfLayoutLoaded guard is defense-in-depth, not a silent edit-mode exit. Design pass. -->
  }
</div>

<!-- Own-self: hold the grid until the layout GET settles, so gridstack inits ONCE over the
     saved layout (a late resync after gridstack adopts the tiles does not reliably reapply
     positions). The drill (!isSelf) never fetches, so it renders immediately. -->
@if (!isSelf || selfLayoutFetched) {
<bst-dashboard-grid [arranging]="layoutEditable" [layout]="editing ? layoutDraft : selfLayout" [idsKey]="visibleSelfKey" [resetTick]="layoutResetTick" [cellHeight]="40" (layoutChange)="layoutDraft = $event">

  <div class="grid-stack-item" [attr.gs-id]="'hours.week'" [attr.gs-x]="cell('hours.week').x" [attr.gs-y]="cell('hours.week').y" [attr.gs-w]="cell('hours.week').w" [attr.gs-h]="cell('hours.week').h" [attr.gs-min-w]="minW('hours.week')">
  <div class="grid-stack-item-content">
    <div class="cfg-w">
    <div class="cfg-bar"><button type="button" class="bst-drag-handle" [attr.aria-label]="'dashboard.self.layout.move' | translate" [title]="'dashboard.self.layout.move' | translate">⠿</button></div>
    <!-- hours.byday is nested here as a FLUSH host (no card chrome, no header) rather than
         rendered as its own tile: the daily bars are evidence for this card's number, not a
         separate destination. One tile, one header, two queries. -->
    <!-- The drawer gets the daily result too: it is already loaded beside this card, so
         the detail view costs nothing and is no longer a copy of the number above it. -->
    <bst-widget-host #h1 [query]="q('hours.week')" [title]="selectedPeriod === 'week' ? 'dashboard.w.hours_week' : 'dashboard.w.hours_month'"
                     [subtitle]="selectedPeriod === 'week' ? 'dashboard.w.hours_week_sub' : 'dashboard.w.hours_month_sub'"
                     [drillable]="true" (drilldown)="openHoursDrill($event)"
                     emptyActionKey="dashboard.empty.log_hours" (emptyAction)="goLogHours()">
      <div class="hours-merged">
        <bst-kpi-card *ngIf="$any(h1.last)?.data as r"
          [value]="r.registered" [suffix]="'/ ' + r.planned"
          [sparkline]="r.sparkline"></bst-kpi-card>
        @if (selectedPeriod === 'week') {
          <bst-widget-host #hb [query]="q('hours.byday')" [flush]="true">
            <bst-week-hours *ngIf="$any(hb.last)?.data as rb" [data]="rb" [compact]="true"></bst-week-hours>
          </bst-widget-host>
        }
      </div>
    </bst-widget-host>
    </div>
  </div>
  </div>

  <div class="grid-stack-item" [attr.gs-id]="'utilization.self'" [attr.gs-x]="cell('utilization.self').x" [attr.gs-y]="cell('utilization.self').y" [attr.gs-w]="cell('utilization.self').w" [attr.gs-h]="cell('utilization.self').h" [attr.gs-min-w]="minW('utilization.self')">
  <div class="grid-stack-item-content">
    <div class="cfg-w">
    <div class="cfg-bar"><button type="button" class="bst-drag-handle" [attr.aria-label]="'dashboard.self.layout.move' | translate" [title]="'dashboard.self.layout.move' | translate">⠿</button></div>
    <bst-widget-host #h2 [query]="q('utilization.self')" title="dashboard.w.utilization" [drillable]="true" (drilldown)="openDrill($event)">
      <bst-kpi-card *ngIf="$any(h2.last)?.data as r"
        [value]="r.value + '%'"
        [bullet]="{ value: r.value, target: r.target }"
        [deltaText]="r.deltaPrevPeriod !== null ? ((r.deltaPrevPeriod >= 0 ? '▲ ' : '▼ ') + r.deltaPrevPeriod + ' ' + ('dashboard.unit.pts' | translate)) : ''"></bst-kpi-card>
    </bst-widget-host>
    </div>
  </div>
  </div>

  <div class="grid-stack-item" [attr.gs-id]="'timesheet.status'" [attr.gs-x]="cell('timesheet.status').x" [attr.gs-y]="cell('timesheet.status').y" [attr.gs-w]="cell('timesheet.status').w" [attr.gs-h]="cell('timesheet.status').h" [attr.gs-min-w]="minW('timesheet.status')">
  <div class="grid-stack-item-content">
    <div class="cfg-w">
    <div class="cfg-bar"><button type="button" class="bst-drag-handle" [attr.aria-label]="'dashboard.self.layout.move' | translate" [title]="'dashboard.self.layout.move' | translate">⠿</button></div>
    <bst-widget-host #h3 [query]="qWeek('timesheet.status')" title="dashboard.w.timesheet" [drillable]="true" (drilldown)="openDrill($event)">
      <!-- Who decided this week and when, next to the status chip. Both branches are
           needed: a decided week whose approver was since deleted keeps the timestamp
           but has no name. -->
      <ng-container *ngIf="$any(h3.last)?.data as r">
        <bst-kpi-card
          [value]="('dashboard.unit.week' | translate) + ' ' + weekNo(r.current.weekStart)"
          [statusChip]="tsChip(r.current.status)"
          [deltaText]="r.current.decidedBy
            ? ('dashboard.timesheet.decided_by' | translate:{ who: r.current.decidedBy, when: (r.current.decidedAt | date:'d MMM') })
            : (r.current.decidedAt ? ('dashboard.timesheet.decided_at' | translate:{ when: (r.current.decidedAt | date:'d MMM') }) : '')"></bst-kpi-card>
        <!-- Last week never got any timesheet action at all - a genuine miss, unlike the
             current week still being open (it just isn't over yet). Direct link straight to
             that week instead of making the user navigate to Timesheet and page back. -->
        <a *ngIf="r.previous.status === 'open'"
           nbButton ghost size="small" class="ts-nudge"
           routerLink="/registration/hours/calendar" [queryParams]="{ week: r.previous.weekStart }"
           (click)="$event.stopPropagation()">
          {{ 'dashboard.timesheet.missing_week_cta' | translate:{ when: (r.previous.weekStart | date:'d MMM') } }}
        </a>
      </ng-container>
    </bst-widget-host>
    </div>
  </div>
  </div>

  <!-- v2.2 cut: on own-self, heatmap moved out of the arranged grid, so it only renders
       as a gridstack item for the member-drill (!isSelf). hours.byday no longer renders as a
       tile anywhere: its bars are drawn inside the hours.week tile above. -->
  @if (!isSelf) {
  <div class="grid-stack-item" [attr.gs-id]="'hours.heatmap'" [attr.gs-x]="cell('hours.heatmap').x" [attr.gs-y]="cell('hours.heatmap').y" [attr.gs-w]="cell('hours.heatmap').w" [attr.gs-h]="cell('hours.heatmap').h" [attr.gs-min-w]="minW('hours.heatmap')">
  <div class="grid-stack-item-content">
    <div class="cfg-w">
    <div class="cfg-bar"><button type="button" class="bst-drag-handle" [attr.aria-label]="'dashboard.self.layout.move' | translate" [title]="'dashboard.self.layout.move' | translate">⠿</button></div>
    <bst-widget-host #h6 [query]="qFixedWindow('hours.heatmap')" title="dashboard.w.heatmap" subtitle="dashboard.w.heatmap_sub" [drillable]="true" (drilldown)="openDrill($event)">
      <bst-hours-heatmap *ngIf="$any(h6.last)?.data as r" [data]="r"></bst-hours-heatmap>
    </bst-widget-host>
    </div>
  </div>
  </div>
  }

  <div class="grid-stack-item" [attr.gs-id]="'assignments.ending'" [attr.gs-x]="cell('assignments.ending').x" [attr.gs-y]="cell('assignments.ending').y" [attr.gs-w]="cell('assignments.ending').w" [attr.gs-h]="cell('assignments.ending').h" [attr.gs-min-w]="minW('assignments.ending')">
  <div class="grid-stack-item-content">
    <div class="cfg-w">
    <div class="cfg-bar"><button type="button" class="bst-drag-handle" [attr.aria-label]="'dashboard.self.layout.move' | translate" [title]="'dashboard.self.layout.move' | translate">⠿</button></div>
    <bst-widget-host #h7 [query]="qFixedWindow('assignments.ending')" title="dashboard.w.ending" subtitle="dashboard.w.ending_sub" [drillable]="true" (drilldown)="openDrill($event)">
      <bst-assignments-ending *ngIf="$any(h7.last)?.data as r" [data]="r"></bst-assignments-ending>
    </bst-widget-host>
    </div>
  </div>
  </div>

  <!-- contribution.self: the manager-drill (isSelf=false) only, and only when the ACTOR
       (viewing manager) holds view_bill_rate for this tenant. Own-self never renders
       contribution.self at all (v2.2 cut: derived money is not part of "Am I done this
       week?"). -->
  @if (!isSelf && actorCanSeeBillRate) {
  <div class="grid-stack-item" [attr.gs-id]="'contribution.self'" [attr.gs-x]="cell('contribution.self').x" [attr.gs-y]="cell('contribution.self').y" [attr.gs-w]="cell('contribution.self').w" [attr.gs-h]="cell('contribution.self').h" [attr.gs-min-w]="minW('contribution.self')">
  <div class="grid-stack-item-content">
    <div class="cfg-w">
    <div class="cfg-bar"><button type="button" class="bst-drag-handle" [attr.aria-label]="'dashboard.self.layout.move' | translate" [title]="'dashboard.self.layout.move' | translate">⠿</button></div>
    <bst-widget-host #h9 [query]="q('contribution.self')" title="dashboard.w.contribution"
                     subtitle="dashboard.w.contribution_sub" [drillable]="true" (drilldown)="openDrill($event)"><!-- subtitle IS the mandatory derived-money label -->
      <bst-kpi-card *ngIf="$any(h9.last)?.data as r"
        [value]="'€ ' + (r.amount | number:'1.0-0')"
        [deltaText]="r.deltaPrevPeriod !== null ? ((r.deltaPrevPeriod >= 0 ? '▲ ' : '▼ ') + r.deltaPrevPeriod + ('dashboard.unit.vs_prev' | translate)) : ''"></bst-kpi-card>
    </bst-widget-host>
    </div>
  </div>
  </div>
  }

  <!-- leave.self: the manager-drill only (v2.2 cut keeps it off own-self). The backend
       scopes the count to leave booked under the ACTOR's tenant and hides legacy
       unattributed rows (Holiday.Client_id, LeaveResolver). Not drillable: its evidence
       is notApplicable and no source screen shows the same tenant-scoped cut. -->
  @if (!isSelf) {
  <div class="grid-stack-item" [attr.gs-id]="'leave.self'" [attr.gs-x]="cell('leave.self').x" [attr.gs-y]="cell('leave.self').y" [attr.gs-w]="cell('leave.self').w" [attr.gs-h]="cell('leave.self').h" [attr.gs-min-w]="minW('leave.self')">
  <div class="grid-stack-item-content">
    <div class="cfg-w">
    <div class="cfg-bar"><button type="button" class="bst-drag-handle" [attr.aria-label]="'dashboard.self.layout.move' | translate" [title]="'dashboard.self.layout.move' | translate">⠿</button></div>
    <bst-widget-host #h10 [query]="qFixedWindow('leave.self')" title="dashboard.w.leave" subtitle="dashboard.w.leave_sub">
      <bst-kpi-card *ngIf="$any(h10.last)?.data as r"
        [value]="'' + r.takenDays"
        [suffix]="'dashboard.unit.taken' | translate"></bst-kpi-card>
    </bst-widget-host>
    </div>
  </div>
  </div>
  }

</bst-dashboard-grid>
}
